import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Laptop, Desktop, DeviceMobile, ArrowRight, WhatsappLogo, MapPin,
  Phone, EnvelopeSimple, InstagramLogo, FacebookLogo,
  TiktokLogo, ShieldCheck, Sparkle, ListBullets, X, Lock, ArrowUpRight,
  Storefront,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import CuratedSection from "@/components/storefront/CuratedSection";
import { CartFloatingButton, CartDrawer } from "@/components/storefront/CartDrawer";

// Inspiration: https://www.behance.net/gallery/244957083 (dark, premium, big type)
// Palette and rules come from /app/design_guidelines.json

const NAV_LINKS = [
  { href: "#ofertas",        label: "Ofertas" },
  { href: "/catalogo",       label: "Catálogo",      external: true, testid: "nav-tienda" },
  { href: "#equipos",        label: "Equipos" },
  { href: "#nosotros",       label: "Nosotros" },
  { href: "#distribuidores", label: "Distribuidores" },
  { href: "#redes",          label: "Redes" },
  { href: "#contacto",       label: "Contacto" },
];

const CATEGORIES = [
  {
    id: "laptops",
    icon: Laptop,
    title: "Laptops Premium",
    subtitle: "Movilidad sin compromisos",
    blurb: "Ultrabooks, workstations y equipos gaming de las marcas más confiables del mercado.",
    image: "https://images.unsplash.com/photo-1771014846919-3a1cf73aeea1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwbGFwdG9wJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgxMjQwMDk5fDA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-7 lg:row-span-2",
  },
  {
    id: "cpus",
    icon: Desktop,
    title: "Desktop PCs",
    subtitle: "Potencia bruta para creadores",
    blurb: "Torres armadas a la medida, estaciones para edición, render y oficina.",
    image: "https://images.unsplash.com/photo-1603481588273-2f908a9a7a1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxnYW1pbmclMjBwYyUyMHRvd2VyJTIwZGFya3xlbnwwfHx8fDE3ODEyNDAwOTl8MA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-5",
  },
  {
    id: "phones",
    icon: DeviceMobile,
    title: "Smartphones",
    subtitle: "Conectividad de última generación",
    blurb: "Equipos iPhone y Android con garantía y soporte directo del distribuidor.",
    image: "https://images.pexels.com/photos/404280/pexels-photo-404280.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-5",
  },
];

const STATS = [
  { value: "10+", label: "Años de experiencia" },
  { value: "100%", label: "Cobertura nacional" },
  { value: "B2B+B2C", label: "Modelo dual" },
];

const VALUE_POINTS = [
  { icon: ShieldCheck, title: "Garantía respaldada", text: "Cada equipo pasa por revisión técnica antes de salir de bodega." },
  { icon: Sparkle,     title: "Selección curada",   text: "Solo trabajamos con marcas que cumplen nuestro estándar de calidad." },
  { icon: ListBullets, title: "Surtido total",      text: "Desde refacciones puntuales hasta proyectos de equipamiento completo." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const HERO_BG = "https://images.unsplash.com/photo-1587202372775-e229f172b9d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxnYW1pbmclMjBwYyUyMHRvd2VyJTIwZGFya3xlbnwwfHx8fDE3ODEyNDAwOTl8MA&ixlib=rb-4.1.0&q=85";

/**
 * Build synthetic curated sections from a flat product list. Used as a
 * graceful fallback when the admin hasn't configured any real Grouping.
 *
 * Strategy:
 *  1. "Destacados" (Hero) — top 4 products by store_price (premium feel).
 *  2. One section per category that has >= 2 products, up to 6 categories.
 *  3. If a product has an active promotion (already attached by the API),
 *     it's rendered as kind="promotion" so the badge + crossed price work.
 *
 * Each item is shaped to match what CuratedSection expects.
 */
function buildFallbackSections(products) {
  if (!Array.isArray(products) || products.length === 0) return [];

  const toItem = (p, order) => {
    const photo = (p.photos || [])[0] || null;
    const currency = p.sale_currency || "MXN";
    // Always emit kind="product" so CuratedSection treats it like a normal
    // product card. If the public API attached an active promotion, we nest
    // it under .promotion so the badge + crossed-out price render correctly.
    const item = {
      kind: "product",
      id: p.id,
      product_id: p.id,
      name: p.name,
      brand: p.brand,
      photo,
      store_price: p.store_price,
      currency,
      order,
    };
    if (p.promotion) {
      item.promotion = {
        id: p.promotion.id,
        promo_price: p.promotion.promo_price,
        currency: p.promotion.currency || currency,
        badge_label: p.promotion.badge_label || "PROMO",
      };
    }
    return item;
  };

  const synth = [];

  // 1) Premium / Destacados — top 4 by price
  const byPrice = [...products].sort((a, b) => (b.store_price || 0) - (a.store_price || 0)).slice(0, 4);
  if (byPrice.length >= 2) {
    synth.push({
      id: "fb-destacados",
      section: "premium",
      name: "Destacados de la semana",
      description: "Lo más premium en stock, listo para envío.",
      accent_color: "#002FA7",
      items: byPrice.map((p, i) => toItem(p, i)),
    });
  }

  // 2) Sections by category (>= 2 items)
  const byCategory = new Map();
  for (const p of products) {
    const cat = p.category || "Otros";
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat).push(p);
  }
  const sortedCats = [...byCategory.entries()]
    .filter(([, arr]) => arr.length >= 2)
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, 6);
  for (const [cat, arr] of sortedCats) {
    synth.push({
      id: `fb-cat-${cat}`,
      section: "custom",
      name: cat,
      description: `Explora nuestra selección de ${cat.toLowerCase()}.`,
      accent_color: "#18181b",
      items: arr.slice(0, 8).map((p, i) => toItem(p, i)),
    });
  }

  // 3) If nothing else qualified, fall back to a single "Catálogo" section
  if (synth.length === 0) {
    synth.push({
      id: "fb-all",
      section: "custom",
      name: "Nuestro catálogo",
      description: "Todo lo que tenemos disponible para ti.",
      accent_color: "#18181b",
      items: products.slice(0, 12).map((p, i) => toItem(p, i)),
    });
  }

  return synth;
}

export default function Landing() {
  const [branding, setBranding] = useState({ company_name: "Alendra PC", company_logo: "", company_phone: "", company_email: "" });
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [sections, setSections] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroFade = useTransform(scrollYProgress, [0, 1], [1, 0]);

  useEffect(() => {
    api.get("/public/branding").then((r) => setBranding(r.data)).catch(() => {});
    // Fetch curated groupings + active promotions in parallel. If there are
    // promotions, we prepend a "PROMOCIONES" section so it renders first — the
    // landing's #ofertas anchor already scrolls here, so featured deals get
    // maximum visibility.
    (async () => {
      const [groupings, promos] = await Promise.all([
        api.get("/public/groupings/sections").then((r) => Array.isArray(r.data) ? r.data : []).catch(() => []),
        api.get("/public/storefront/promotions").then((r) => (r.data?.products || [])).catch(() => []),
      ]);
      let base = groupings;
      if (base.length === 0) {
        try {
          const { data } = await api.get("/public/storefront");
          base = buildFallbackSections(data?.products || []);
        } catch { base = []; }
      }
      const promoSection = promos.length > 0 ? [{
        id: "promotions-featured",
        section: "custom",
        name: "Promociones activas",
        description: `${promos.length} oferta${promos.length === 1 ? "" : "s"} vigentes — precios por tiempo limitado`,
        accent_color: "#E0000F",
        items: promos.map((p) => {
          // photos may come in as `photos` array, `photos_url` legacy string, or missing.
          // Some legacy docs also have `photos: [""]` or `[null]` — filter falsy entries.
          const cleanedPhotos = Array.isArray(p.photos) ? p.photos.filter(Boolean) : [];
          const photos = cleanedPhotos.length > 0
            ? cleanedPhotos
            : (p.photos_url ? [p.photos_url] : []);
          return {
            kind: "product",
            id: p.id,
            name: p.name,
            brand: p.brand,
            category: p.category,
            photo: photos[0] || null,   // CuratedItem reads singular `photo`
            photos,                     // keep array for other consumers
            store_price: p.store_price,
            sale_currency: p.sale_currency || "MXN",
            free_shipping: !!p.free_shipping,
            promotion: p.promotion || null,
            stock: p.stock,
          };
        }),
      }] : [];
      setSections([...promoSection, ...base]);
    })();
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  const phone = (branding.company_phone || "").replace(/\D/g, "");
  const waLink = phone ? `https://wa.me/${phone.startsWith("52") ? phone : "52" + phone}` : "#";

  return (
    <div className="min-h-screen bg-[#050505] text-white font-body antialiased selection:bg-[#002FA7] selection:text-white" data-testid="landing-root">
      {/* ============ NAVBAR ============ */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "backdrop-blur-xl bg-[#050505]/80 border-b border-white/5"
            : "bg-transparent"
        }`}
        data-testid="nav-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            {branding.company_logo ? (
              <img src={branding.company_logo} alt="APC" className="h-7 w-7 object-contain" />
            ) : (
              <div className="h-7 w-7 rounded bg-white text-[#050505] flex items-center justify-center font-bold text-xs tracking-tighter">APC</div>
            )}
            <span className="font-display font-semibold tracking-tight text-[15px] group-hover:text-white/90 transition">Alendra PC</span>
          </a>

          <nav className="hidden md:flex items-center gap-9 text-sm text-zinc-400">
            {NAV_LINKS.map((l) => (
              l.external ? (
                <Link key={l.href} to={l.href} className="hover:text-white transition-colors duration-200 inline-flex items-center gap-1.5 font-semibold text-white/90" data-testid={l.testid || `nav-${l.label.toLowerCase()}`}>
                  <Storefront size={14} weight="bold" /> {l.label}
                </Link>
              ) : (
                <a key={l.href} href={l.href} className="hover:text-white transition-colors duration-200" data-testid={`nav-${l.label.toLowerCase()}`}>
                  {l.label}
                </a>
              )
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/portal/login"
              className="text-xs tracking-wider uppercase text-zinc-400 hover:text-white transition px-3 py-1.5 border border-white/10 hover:border-white/30 rounded-full"
              data-testid="nav-portal-cta"
            >
              Portal B2B
            </Link>
            <Link to="/login" className="text-xs text-zinc-500 hover:text-white transition inline-flex items-center gap-1" data-testid="nav-staff-login">
              <Lock size={12} /> Acceso staff
            </Link>
          </div>

          <button
            onClick={() => setNavOpen((o) => !o)}
            className="md:hidden p-2 -mr-2 text-white"
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {navOpen ? <X size={20} /> : (
              <div className="flex flex-col gap-1.5">
                <span className="w-5 h-px bg-white" />
                <span className="w-5 h-px bg-white" />
              </div>
            )}
          </button>
        </div>

        {/* Mobile drawer */}
        {navOpen && (
          <div className="md:hidden bg-[#050505] border-b border-white/5">
            <nav className="px-6 py-6 flex flex-col gap-5 text-zinc-300">
              {NAV_LINKS.map((l) => (
                l.external ? (
                  <Link key={l.href} to={l.href} onClick={() => setNavOpen(false)} className="text-lg font-semibold text-white inline-flex items-center gap-2" data-testid={`m${l.testid || `nav-${l.label.toLowerCase()}`}`}>
                    <Storefront size={18} weight="bold" /> {l.label}
                  </Link>
                ) : (
                  <a key={l.href} href={l.href} onClick={() => setNavOpen(false)} className="text-lg" data-testid={`mnav-${l.label.toLowerCase()}`}>
                    {l.label}
                  </a>
                )
              ))}
              <div className="pt-3 mt-2 border-t border-white/5 flex flex-col gap-3">
                <Link to="/portal/login" className="text-sm uppercase tracking-widest text-white">Portal B2B</Link>
                <Link to="/login" className="text-xs text-zinc-500 inline-flex items-center gap-1"><Lock size={12} /> Acceso staff</Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      <span id="top" />

      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-end overflow-hidden">
        <motion.div
          style={{ y: heroY, opacity: heroFade }}
          className="absolute inset-0 z-0"
        >
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/60 to-[#050505]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(0,47,167,0.25),transparent_55%)]" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pb-20 md:pb-32 w-full">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-zinc-400">
            <span className="w-8 h-px bg-zinc-500" />
            <span>{branding.company_name}</span>
          </motion.div>
          <motion.h1
            initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.1 }}
            className="font-display mt-6 text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tighter leading-[1.02] max-w-4xl"
            data-testid="hero-headline"
          >
            Hardware de élite. <br />
            <span className="text-zinc-500">Rendimiento absoluto.</span>
          </motion.h1>
          <motion.p
            initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.2 }}
            className="mt-8 max-w-xl text-base md:text-lg text-zinc-400 leading-relaxed"
          >
            Distribuidores de tecnología premium en México. Equipamos a profesionales, empresas y entusiastas con el hardware más avanzado del mercado — con la garantía y el respaldo que tu inversión merece.
          </motion.p>
          <motion.div
            initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.3 }}
            className="mt-12 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-zinc-500"
          >
            <span>Desplázate</span>
            <span className="w-12 h-px bg-zinc-600" />
          </motion.div>
        </div>
      </section>

      {/* ============ CURATED SECTIONS (dynamic groupings from admin) ============ */}
      {sections.length > 0 && (
        <section id="ofertas" className="py-20 md:py-28 border-t border-white/5 bg-gradient-to-b from-[#050505] via-[#070710] to-[#050505]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
              className="mb-12 flex items-end justify-between flex-wrap gap-6"
            >
              <div className="max-w-2xl">
                <p className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-4">Lo que se mueve</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
                  Selecciones <span className="text-zinc-500">curadas.</span>
                </h2>
              </div>
              <p className="text-zinc-400 max-w-md leading-relaxed">
                Ofertas, combos y equipos destacados que actualizamos cada semana. Tienes el carrito a la mano para asegurarlos al instante.
              </p>
            </motion.div>

            <div className="space-y-16">
              {sections.map((sec) => (
                <motion.div
                  key={`${sec.section}-${sec.id}`}
                  initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={fadeUp}
                >
                  <CuratedSection section={sec} theme="dark" />
                </motion.div>
              ))}
            </div>

            <div className="mt-16 flex justify-center">
              <Link to="/catalogo" data-testid="landing-storefront-cta">
                <Button size="lg" className="bg-white text-[#050505] hover:bg-zinc-200 px-8 py-6 text-sm uppercase tracking-[0.2em] font-semibold rounded-full gap-2">
                  Ver toda la tienda <ArrowRight size={16} weight="bold" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ============ CATEGORIES BENTO ============ */}
      <section id="equipos" className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
            className="flex items-end justify-between flex-wrap gap-6 mb-14"
          >
            <div className="max-w-2xl">
              <p className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-4">Catálogo</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
                Equipamiento <span className="text-zinc-500">superior.</span>
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md leading-relaxed">
              Tres frentes, una sola obsesión: que cada cliente reciba la combinación correcta de potencia, garantía y precio.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-2 gap-5">
            {CATEGORIES.map((c, idx) => {
              const Icon = c.icon;
              return (
                <motion.article
                  key={c.id}
                  initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
                  variants={fadeUp} transition={{ delay: idx * 0.1 }}
                  className={`group relative overflow-hidden rounded-2xl border border-white/5 hover:border-white/20 bg-[#0a0a0a] aspect-[5/4] lg:aspect-auto ${c.span}`}
                  data-testid={`cat-card-${c.id}`}
                >
                  <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
                  <div className="relative z-10 p-7 h-full flex flex-col justify-end">
                    <Icon size={28} weight="thin" className="text-white/60 mb-4 group-hover:text-white transition" />
                    <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight">{c.title}</h3>
                    <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-2">{c.subtitle}</p>
                    <p className="text-sm text-zinc-400 mt-3 max-w-md leading-relaxed">{c.blurb}</p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      <section id="nosotros" className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} className="lg:col-span-7">
            <p className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-4">Acerca de Nosotros</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              Más que distribuidores, somos el <span className="text-zinc-500">motor de tu negocio.</span>
            </h2>
            <p className="mt-8 text-zinc-400 leading-relaxed max-w-xl">
              Desde Baja California para todo México, Alendra PC se ha consolidado como un referente en distribución de tecnología. Seleccionamos cuidadosamente cada componente para garantizar calidad, garantía y el mejor costo-beneficio del mercado.
            </p>
            <p className="mt-4 text-zinc-400 leading-relaxed max-w-xl">
              Trabajamos con clientes finales que buscan equipos de confianza, y con resellers que necesitan precios competitivos e inventario constante para crecer su propio negocio.
            </p>

            <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {VALUE_POINTS.map((v, idx) => {
                const Icon = v.icon;
                return (
                  <div key={idx} className="border-t border-white/10 pt-4">
                    <Icon size={22} weight="thin" className="text-[#4d6dd1] mb-3" />
                    <h4 className="font-display text-base font-medium tracking-tight">{v.title}</h4>
                    <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{v.text}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} transition={{ delay: 0.15 }} className="lg:col-span-5 flex flex-col gap-4">
            {STATS.map((s) => (
              <div key={s.label} className="border border-white/8 rounded-2xl p-8 bg-gradient-to-br from-white/[0.02] to-transparent">
                <div className="font-display text-5xl md:text-6xl font-bold tracking-tighter">{s.value}</div>
                <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 mt-3">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============ DISTRIBUTORS BANNER ============ */}
      <section id="distribuidores" className="py-24 md:py-32 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,47,167,0.25),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.p initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-6">
            Para Mayoristas
          </motion.p>
          <motion.h2
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.04] max-w-4xl mx-auto"
          >
            Escala tus ventas <br />
            <span className="text-zinc-500">con nosotros.</span>
          </motion.h2>
          <motion.p
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.2 }}
            className="mt-8 text-zinc-400 max-w-2xl mx-auto leading-relaxed"
          >
            Accede a precios preferenciales, inventario en tiempo real y soporte dedicado para nuestra red de distribuidores asociados. Una plataforma diseñada para resellers que mueven volumen.
          </motion.p>
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.3 }}
            className="mt-12 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/portal/login" data-testid="dist-portal-cta">
              <Button size="lg" className="bg-white text-[#050505] hover:bg-zinc-200 px-8 py-6 text-sm uppercase tracking-[0.2em] font-semibold rounded-full gap-2">
                Acceder al portal B2B <ArrowRight size={16} weight="bold" />
              </Button>
            </Link>
            <a href="#contacto" data-testid="dist-apply-cta">
              <Button variant="outline" size="lg" className="border-white/20 text-white hover:bg-white/5 hover:text-white px-8 py-6 text-sm uppercase tracking-[0.2em] font-semibold rounded-full bg-transparent">
                Solicitar acceso
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* ============ SOCIAL ============ */}
      <SocialSection branding={branding} />

      {/* ============ CONTACT ============ */}
      <section id="contacto" className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-4">Contacto</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.04]">
              Hablemos de <br /><span className="text-zinc-500">tecnología.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
              ¿Buscas equipar tu empresa o quieres unirte a nuestra red de distribuidores? Cuéntanos qué necesitas — respondemos en menos de un día hábil.
            </p>

            <div className="mt-12 space-y-5 text-sm text-zinc-300">
              {branding.company_phone && (
                <a href={`tel:${branding.company_phone}`} className="flex items-center gap-3 hover:text-white transition group" data-testid="contact-phone">
                  <span className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center group-hover:border-white/30 transition"><Phone size={14} /></span>
                  {branding.company_phone}
                </a>
              )}
              {branding.company_email && (
                <a href={`mailto:${branding.company_email}`} className="flex items-center gap-3 hover:text-white transition group" data-testid="contact-email">
                  <span className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center group-hover:border-white/30 transition"><EnvelopeSimple size={14} /></span>
                  {branding.company_email}
                </a>
              )}
              {branding.company_address && (
                <div className="flex items-start gap-3 text-zinc-400">
                  <span className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center mt-0.5 shrink-0"><MapPin size={14} /></span>
                  <span>{branding.company_address}{branding.company_city ? `, ${branding.company_city}` : ""}{branding.company_country ? `, ${branding.company_country}` : ""}</span>
                </div>
              )}
            </div>

            {phone && (
              <a href={waLink} target="_blank" rel="noreferrer" className="mt-10 inline-flex items-center gap-3 px-6 py-3.5 bg-[#25D366]/90 hover:bg-[#25D366] text-[#050505] rounded-full text-sm uppercase tracking-[0.2em] font-bold transition" data-testid="contact-whatsapp">
                <WhatsappLogo size={18} weight="fill" /> Chat por WhatsApp
              </a>
            )}
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} transition={{ delay: 0.15 }} className="lg:col-span-7">
            <ContactForm />
          </motion.div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <div className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-none">
                Alendra <span className="text-zinc-700">PC.</span>
              </div>
              <p className="text-zinc-500 text-sm mt-3 max-w-md">Distribuidores premium de tecnología en México.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-6 text-sm text-zinc-500">
              <a href="#nosotros" className="hover:text-white transition">Nosotros</a>
              <a href="#distribuidores" className="hover:text-white transition">Distribuidores</a>
              <a href="#contacto" className="hover:text-white transition">Contacto</a>
              <Link to="/portal/login" className="hover:text-white transition">Portal B2B</Link>
              <Link to="/esquemadeinversion" className="hover:text-white transition" data-testid="footer-link-invest">Invierte</Link>
              <Link to="/esquemadecomisiones" className="hover:text-white transition" data-testid="footer-link-connect">Comisiones</Link>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-zinc-600">
            <span>© {new Date().getFullYear()} {branding.company_name}. Todos los derechos reservados.</span>
            <div className="flex gap-3">
              {branding.social_instagram && (
                <a href={branding.social_instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:border-white/30 hover:text-white transition">
                  <InstagramLogo size={14} />
                </a>
              )}
              {branding.social_facebook && (
                <a href={branding.social_facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:border-white/30 hover:text-white transition">
                  <FacebookLogo size={14} />
                </a>
              )}
              {branding.social_tiktok && (
                <a href={branding.social_tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:border-white/30 hover:text-white transition">
                  <TiktokLogo size={14} />
                </a>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* Floating cart (active when there are curated sections / shoppable items) */}
      <div className="fixed bottom-5 right-5 z-40">
        <CartFloatingButton />
      </div>
      <CartDrawer />
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Llena los campos requeridos");
      return;
    }
    setSending(true);
    try {
      await api.post("/public/lead", form);
      toast.success("Gracias, te contactaremos pronto.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos enviar tu mensaje");
    } finally {
      setSending(false);
    }
  };

  const inp = "bg-transparent border-white/15 text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-white/40 h-12 rounded-none";

  return (
    <form onSubmit={submit} className="border border-white/8 rounded-2xl p-7 md:p-10 bg-white/[0.02] space-y-5" data-testid="landing-contact-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Nombre *</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inp} mt-2`} placeholder="¿Cómo te llamas?" data-testid="lead-name" required />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Correo *</label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inp} mt-2`} placeholder="tucorreo@empresa.com" data-testid="lead-email" required />
        </div>
      </div>
      <div>
        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Teléfono</label>
        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`${inp} mt-2`} placeholder="Opcional" data-testid="lead-phone" />
      </div>
      <div>
        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Mensaje *</label>
        <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="mt-2 bg-transparent border-white/15 text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-white/40 rounded-none resize-none" placeholder="Cuéntanos qué buscas o si te interesa el portal de distribuidores…" data-testid="lead-message" required />
      </div>
      <Button type="submit" disabled={sending} className="w-full sm:w-auto bg-white text-[#050505] hover:bg-zinc-200 px-10 py-6 text-sm uppercase tracking-[0.25em] font-semibold rounded-full gap-2 mt-2" data-testid="lead-submit">
        {sending ? "Enviando…" : "Enviar mensaje"} <ArrowRight size={14} weight="bold" />
      </Button>
    </form>
  );
}

/* ============================================================== */
/*                       SOCIAL SECTION                            */
/* ============================================================== */

// Pull the @handle out of any platform URL we know about.
const handleFromUrl = (url, platform) => {
  if (!url) return "";
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/^\/+|\/+$/g, "");  // strip slashes
    const seg = path.split("/")[0] || "";
    return seg.startsWith("@") ? seg : (platform === "tiktok" ? "@" + seg : "@" + seg);
  } catch { return ""; }
};

function SocialSection({ branding }) {
  const items = [
    {
      key: "instagram",
      name: "Instagram",
      handle: handleFromUrl(branding.social_instagram, "instagram"),
      url: branding.social_instagram,
      tagline: "Drops, equipos en vivo y detrás de cámara",
      Icon: InstagramLogo,
      // Authentic IG gradient
      bg: "linear-gradient(135deg, #515BD4 0%, #8134AF 30%, #DD2A7B 60%, #FEDA77 100%)",
      ring: "ring-[#DD2A7B]/40",
      grid: [
        "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=400&q=70",
        "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=400&q=70",
        "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?auto=format&fit=crop&w=400&q=70",
        "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=400&q=70",
      ],
    },
    {
      key: "facebook",
      name: "Facebook",
      handle: handleFromUrl(branding.social_facebook, "facebook"),
      url: branding.social_facebook,
      tagline: "Promociones, reseñas y comunidad",
      Icon: FacebookLogo,
      bg: "linear-gradient(140deg, #0866FF 0%, #002FA7 60%, #001A66 100%)",
      ring: "ring-[#0866FF]/40",
      stats: [
        { label: "Página", value: "Verificada" },
        { label: "Categoría", value: "Tecnología" },
      ],
    },
    {
      key: "tiktok",
      name: "TikTok",
      handle: handleFromUrl(branding.social_tiktok, "tiktok"),
      url: branding.social_tiktok,
      tagline: "Tips, unboxings y momentos virales",
      Icon: TiktokLogo,
      bg: "linear-gradient(135deg, #000 0%, #25F4EE 35%, #FE2C55 100%)",
      ring: "ring-[#FE2C55]/40",
      videos: 3,
    },
  ];

  // If no socials are configured at all, hide the section.
  if (!items.some((i) => i.url)) return null;

  return (
    <section id="redes" className="py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} className="mb-14 max-w-2xl">
          <p className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-4">Síguenos</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
            En vivo en <span className="text-zinc-500">nuestras redes.</span>
          </h2>
          <p className="text-zinc-400 mt-6 leading-relaxed">
            Equipos, ofertas, contenido técnico y la comunidad que estamos construyendo. Únete donde mejor te acomode.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((item, idx) => item.url && (
            <motion.a
              key={item.key}
              href={item.url}
              target="_blank"
              rel="noreferrer"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -6 }}
              className={`group relative overflow-hidden rounded-3xl ring-1 ${item.ring} aspect-[4/5] block`}
              data-testid={`social-card-${item.key}`}
            >
              {/* gradient background */}
              <div className="absolute inset-0" style={{ background: item.bg }} />
              {/* noise overlay */}
              <div className="absolute inset-0 opacity-[0.15] mix-blend-overlay" style={{
                backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')",
              }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/20" />

              <div className="relative z-10 h-full p-6 md:p-7 flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <item.Icon size={22} weight="fill" className="text-white" />
                    <span className="text-white font-display font-medium tracking-tight">{item.name}</span>
                  </div>
                  <span className="opacity-70 group-hover:opacity-100 group-hover:rotate-45 transition-transform duration-300">
                    <ArrowUpRight size={20} weight="bold" className="text-white" />
                  </span>
                </div>

                {/* Platform-specific creative preview */}
                <div className="flex-1 my-5 relative">
                  {item.key === "instagram" && (
                    <div className="grid grid-cols-2 gap-1.5 absolute inset-0">
                      {item.grid.map((src, i) => (
                        <div key={i} className="relative overflow-hidden rounded-md bg-white/10">
                          <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition" />
                        </div>
                      ))}
                    </div>
                  )}

                  {item.key === "facebook" && (
                    <div className="absolute inset-0 flex flex-col justify-center gap-3">
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-7 h-7 rounded-full bg-white/30" />
                          <div className="flex-1">
                            <div className="text-xs text-white font-semibold truncate">{item.handle.replace("@","")}</div>
                            <div className="text-[10px] text-white/60">Página · Tecnología</div>
                          </div>
                          <div className="text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full">✓ Verificada</div>
                        </div>
                        <div className="space-y-1.5">
                          <div className="h-1.5 bg-white/20 rounded-full w-full" />
                          <div className="h-1.5 bg-white/20 rounded-full w-3/4" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {item.stats.map((s) => (
                          <div key={s.label} className="bg-white/8 backdrop-blur-sm rounded-lg px-3 py-2">
                            <div className="text-[10px] uppercase tracking-wider text-white/60">{s.label}</div>
                            <div className="text-sm font-semibold text-white mt-0.5">{s.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {item.key === "tiktok" && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex gap-2 -rotate-6">
                        {[...Array(item.videos)].map((_, i) => (
                          <div key={i} className={`w-20 h-32 rounded-xl border border-white/20 shadow-2xl flex items-end justify-center p-2 ${i === 1 ? "scale-110 z-10 rotate-3" : "opacity-80"}`}
                               style={{ background: "linear-gradient(135deg, #25F4EE40, #FE2C5540)" }}>
                            <div className="text-[9px] text-white/80 font-mono-data">▶ {Math.floor(Math.random()*900+100)}K</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <div className="font-display text-2xl md:text-3xl font-bold tracking-tighter text-white">{item.handle}</div>
                  <p className="text-xs text-white/70 mt-1.5 leading-relaxed">{item.tagline}</p>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}

